/**
 * The record: who a World knows (occupiers, hands, relays, asks), what the
 * World is (describe), and the account around it (me, grants, keys, webhooks,
 * usage, billing). Records say who and what, never which bits: occupancy is
 * the planes' alone. Each kind is refetched when the reconciler says an act
 * changed it, one read at a time per kind, and any read the hand makes
 * through the client (a Lens on the Roster, say) is taken in as it passes.
 *
 * A World is named as the URL names it. A World of persona B or C is written
 * `B:mill`; a bare name is persona A's.
 */

import type { Args, CallRecord, Client, CreateRecordStore, Json, Persona, RecordKind, RecordStore, Unsub } from '../contracts.ts';
import { worldKey, worldRef } from '../worlds.ts';

export { worldKey, worldRef };

/** Which read fills which kind. World kinds take `w`; the others name the account. */
export const READS: Readonly<Record<RecordKind, { op: string; world: boolean }>> = {
  me: { op: 'whoami', world: false },
  describe: { op: 'getWorld', world: true },
  occupiers: { op: 'listOccupiers', world: true },
  hands: { op: 'listHands', world: true },
  relays: { op: 'listRelays', world: true },
  asks: { op: 'listAsks', world: true },
  grants: { op: 'listGrants', world: false },
  keys: { op: 'listKeys', world: false },
  webhooks: { op: 'listWebhooks', world: false },
  usage: { op: 'usage', world: false },
  billing: { op: 'billing', world: false },
};

/** Answers that are a record whole, whoever asked for them: the op → the kind it fills. */
const ABSORBS: Readonly<Record<string, RecordKind>> = Object.fromEntries(
  (Object.entries(READS) as [RecordKind, { op: string }][]).map(([kind, { op }]) => [op, kind]),
);

export interface RecordModel extends RecordStore {
  /** The last problem a refresh met, if the last one failed. */
  problem(world: string | null, kind: RecordKind): Json | undefined;
}

const slot = (world: string | null, kind: RecordKind): string => `${world ?? ''}\u0000${kind}`;

export const createRecordModel = (client: Client): RecordModel => {
  const values = new Map<string, Json>();
  const problems = new Map<string, Json>();
  const listeners = new Set<(world: string | null, kind: RecordKind) => void>();
  /** One read in flight per kind; a refresh asked for meanwhile runs once more after it. */
  const inFlight = new Map<string, Promise<void>>();
  const again = new Set<string>();

  const tell = (world: string | null, kind: RecordKind): void => {
    for (const fn of [...listeners]) {
      try { fn(world, kind); } catch { /* a listener's fault is its own */ }
    }
  };

  const store = (world: string | null, kind: RecordKind, value: Json): void => {
    const at = slot(world, kind);
    problems.delete(at);
    const before = values.get(at);
    values.set(at, value);
    if (before === undefined || JSON.stringify(before) !== JSON.stringify(value)) tell(world, kind);
  };

  const argsFor = (world: string | null, kind: RecordKind): { args: Args; persona: Persona } | null => {
    const read = READS[kind];
    if (!read.world) return { args: {}, persona: world === null ? 'A' : worldRef(world).persona };
    if (world === null) return null;
    const { persona, w } = worldRef(world);
    return { args: { w }, persona };
  };

  const readOnce = async (world: string | null, kind: RecordKind): Promise<void> => {
    const call = argsFor(world, kind);
    if (!call) return;
    const record = await client.call(READS[kind].op, call.args, { persona: call.persona, source: { kind: 'auto' } });
    const answer = record.answer;
    if (answer && answer.status >= 200 && answer.status < 300) store(world, kind, answer.body);
    else if (answer) problems.set(slot(world, kind), answer.body);
  };

  const refresh = (world: string | null, kind: RecordKind): Promise<void> => {
    const at = slot(world, kind);
    const running = inFlight.get(at);
    if (running) {
      again.add(at);
      return running;
    }
    const run = (async () => {
      try {
        do {
          again.delete(at);
          await readOnce(world, kind);
        } while (again.has(at));
      } finally {
        inFlight.delete(at);
      }
    })();
    inFlight.set(at, run);
    return run;
  };

  /** A read the hand or anyone made through the client: the answer is the record, so keep it. */
  const absorb = (record: CallRecord): void => {
    const kind = ABSORBS[record.op];
    const answer = record.answer;
    if (!kind || !answer || answer.status < 200 || answer.status >= 300) return;
    // The Director's results reach us clipped to fit its context: never a whole record.
    if (record.source.kind === 'director') return;
    if (READS[kind].world) {
      const w = record.args.w;
      if (typeof w === 'string') store(worldKey(record.persona, w), kind, answer.body);
    } else if (record.persona === 'A') {
      store(null, kind, answer.body);
    }
  };
  client.on((record, phase) => {
    if (phase === 'answered') absorb(record);
    // createWorld answers with the World itself.
    if (phase === 'answered' && record.op === 'createWorld' && record.answer?.status === 201 && record.source.kind !== 'director') {
      const body = record.answer.body;
      const name = body && typeof body === 'object' && !Array.isArray(body) ? body.name : null;
      if (typeof name === 'string') store(worldKey(record.persona, name), 'describe', body);
    }
  });

  return {
    get: (world, kind) => values.get(slot(world, kind)),
    refresh,
    on(fn): Unsub {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
    put: store,
    problem: (world, kind) => problems.get(slot(world, kind)),
  };
};

export const createRecordStore: CreateRecordStore = createRecordModel;

// -- reading records ------------------------------------------------------------------

export interface OccupierRecord {
  readonly id: number;
  readonly kind: 'holder' | 'event' | 'series' | 'foreign';
  readonly name: string;
  readonly rank: number;
  readonly pinned: boolean;
  readonly holder: string | null;
  readonly keeper: boolean;
}

/** `listOccupiers`' answer, read loosely; anything malformed is left out. */
export const occupiersOf = (value: Json | undefined): OccupierRecord[] => {
  const list = value && typeof value === 'object' && !Array.isArray(value) ? value.occupiers : null;
  if (!Array.isArray(list)) return [];
  return list.flatMap((o) => {
    if (!o || typeof o !== 'object' || Array.isArray(o) || typeof o.id !== 'number' || typeof o.name !== 'string') return [];
    const kind = o.kind === 'event' || o.kind === 'series' || o.kind === 'foreign' ? o.kind : 'holder';
    return [{
      id: o.id, kind, name: o.name,
      rank: typeof o.rank === 'number' ? o.rank : 0,
      pinned: o.pinned === true,
      holder: typeof o.holder === 'string' ? o.holder : null,
      keeper: o.keeper === true,
    }];
  });
};

/** A World's describe (`getWorld`, or a `hello`), read loosely. */
export interface Describe {
  readonly globalName: string; readonly preset: string; readonly leafSlotBits: number; readonly allowDisplace: boolean;
  readonly tieBreak: string; readonly seats: number; readonly seq: number; readonly head: string;
}

export const describeOf = (value: Json | undefined): Describe | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const v = value;
  if (typeof v.leafSlotBits !== 'number') return null;
  return {
    globalName: typeof v.globalName === 'string' ? v.globalName : '',
    preset: typeof v.preset === 'string' ? v.preset : 'temporal-hour',
    leafSlotBits: v.leafSlotBits,
    allowDisplace: v.allowDisplace === true,
    tieBreak: typeof v.tieBreak === 'string' ? v.tieBreak : 'incumbent',
    seats: typeof v.seats === 'number' ? v.seats : 1,
    seq: typeof v.seq === 'number' ? v.seq : 0,
    head: typeof v.head === 'string' ? v.head : '',
  };
};
